/**
 * Multi-building campus map geometry (see docs/design/layout-refs/).
 *
 * Grid: MAP_COLS × MAP_ROWS (isometric engine).
 * Department floors host personal pods; campus is the outdoor hub.
 */

export {
  type DoorWall,
  MAP_COLS,
  MAP_ROWS,
  makePodFromBounds,
  type PersonalOfficeSlotLayout,
} from "@/lib/office/pod-bounds";

import type { DoorWall, PersonalOfficeSlotLayout } from "@/lib/office/pod-bounds";

/** Max floor decor pieces an owner can place (matches schema max). */
export const MAX_OFFICE_DECOR = 4;
/** Max framed wall photos per pod. */
export const MAX_WALL_ART = 2;

export type CommonZoneLayout = {
  id: string;
  name: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  rug: string;
  /** Hard A/V isolation + enclosed walls. */
  priv?: boolean;
  /**
   * How the world engine furnishes a private room:
   * - meeting — large all-hands table + many seats + screen wall
   * - huddle — small table + few seats
   */
  roomKind?: "meeting" | "huddle";
};

/**
 * Shared amenity zones on the center floor plate (inward of the office ring).
 *
 * Layout (rough, matches hq-interior-ref):
 *   [Huddle]   [ Reception floor ]   [ Conference A (glass) ]
 *   [ Lounge open sofas ]            [ Conference B (glass) ]
 *   [ Focus open ]
 *
 * Walk into any `priv` room → hear everyone in that same room at full volume
 * (see `computeSpatialVolume` in spatial-volume.ts).
 */
export const COMMON_ZONES: readonly CommonZoneLayout[] = [
  // Conference A — glass meeting (east, upper)
  {
    id: "meeting",
    name: "Conference A",
    x1: 34,
    y1: 8,
    x2: 47,
    y2: 18,
    rug: "#DCECF8",
    priv: true,
    roomKind: "meeting",
  },
  // Conference B — second glass room (east, lower)
  {
    id: "conf_b",
    name: "Conference B",
    x1: 34,
    y1: 20,
    x2: 47,
    y2: 29,
    rug: "#D8E8F4",
    priv: true,
    roomKind: "meeting",
  },
  // Small huddle — NW of the plate
  {
    id: "huddle_a",
    name: "Huddle",
    x1: 8,
    y1: 8,
    x2: 14,
    y2: 14,
    rug: "#E8E0F4",
    priv: true,
    roomKind: "huddle",
  },
  // Reception floor (open — furniture placed via layout.furniture)
  {
    id: "reception",
    name: "Reception",
    x1: 23,
    y1: 12,
    x2: 33,
    y2: 22,
    // Cool blue-gray lobby tile (ref-like)
    rug: "#D4E4F2",
  },
  // Lounge — purple/green sofa clusters (open social)
  {
    id: "lounge",
    name: "The Lounge",
    x1: 8,
    y1: 16,
    x2: 22,
    y2: 30,
    // Soft lavender lounge carpet
    rug: "#E8DCF0",
  },
  // Focus open work area south of lounge
  {
    id: "focus",
    name: "Focus Zone",
    x1: 10,
    y1: 31,
    x2: 28,
    y2: 35,
    rug: "#E4E6F2",
  },
] as const;

/** True for enclosed common rooms (not personal offices). */
export function isPrivateCommonRoomId(zoneId: string): boolean {
  return COMMON_ZONES.some((z) => z.id === zoneId && z.priv === true);
}

/** All private common room ids (meeting + huddles). */
export const PRIVATE_COMMON_ROOM_IDS: readonly string[] = COMMON_ZONES.filter((z) => z.priv).map(
  (z) => z.id,
);

/**
 * Free furniture kinds the world engine can draw (admin palette + default layout).
 * Prefer 1×1 kinds for sprites; multi-tile (table/counter/conftable/screen) stay procedural.
 */
export const MAP_FURNITURE_KINDS = [
  // Core / layout
  "desk",
  "chair",
  "plant",
  "couch",
  "sofa1",
  "table",
  "counter",
  "cooler",
  "conftable",
  "screen",
  "podium",
  "cushion",
  "fox",
  "arcade",
  "trophy",
  "sign",
  "pod",
  // Walls / doors (personal offices + campus building shells)
  "wall",
  "gwall",
  "gwallW",
  "door",
  // Outdoor campus (plaza) — not indoor office props
  "building",
  "tree",
  "bush",
  "bench",
  "streetlamp",
  "fountain",
  "plaza_sign",
  "delivery_truck",
  "cafe_umbrella",
  // Outdoor nature / street furniture (Habbo-style heroes)
  "pine",
  "rock",
  "flower",
  "hedge",
  "planter",
  "bollard",
  "fence",
  "flowerbed",
  // Expanded catalog (sprites + decor unlocks)
  "lamp",
  "cactus",
  "bookshelf",
  "beanbag",
  "whiteboard",
  "coffee",
  "printer",
  "bin",
  "clock",
  "fridge",
  "locker",
  "guitar",
  "bike",
  "fishbowl",
  "easel",
  "flag",
  "neon",
  "mic",
  "stool",
  "jukebox",
  "laptop",
  "server",
  "mat",
  "statue",
  "rug",
  // Layout redesign heroes
  "reception",
  "coffeetable",
] as const;
export type MapFurnitureKind = (typeof MAP_FURNITURE_KINDS)[number];

/** True when `kind` is a known map furniture / decor sprite id. */
export function isMapFurnitureKind(kind: string): kind is MapFurnitureKind {
  return (MAP_FURNITURE_KINDS as readonly string[]).includes(kind);
}

export type MapFurniture = {
  id: string;
  kind: MapFurnitureKind;
  gx: number;
  gy: number;
  w: number;
  d: number;
  dir: DoorWall;
  /** When true, tile is a sit target. */
  seat?: boolean;
  /** When true, tile blocks walking. */
  block?: boolean;
  /** Optional draw tint (campus building shells). */
  c?: string;
  /** Optional label for entrance doors / building names (campus). */
  displayName?: string;
  /** Outdoor building facade door side (N/S/E/W). */
  doorWall?: DoorWall;
  /**
   * Outdoor building facade style key (cafe, delivery, …).
   * Used by sprite catalog variants + procedural style drawers.
   */
  style?: string;
};

/** Walk-onto tile that loads another map and teleports the player. */
export type MapTransition = {
  id: string;
  /** Transition trigger tile (single cell for v1). */
  gx: number;
  gy: number;
  toMapId: string;
  toSpawn: { gx: number; gy: number };
  label?: string;
};

/** Re-export map catalog (campus multi-building). */
export {
  DEFAULT_JOIN_MAP_ID,
  getMapMeta,
  isKnownMapId,
  isOutdoorMap,
  MAP_IDS,
  MAP_REGISTRY,
  type MapId,
  type MapMeta,
  type MapTheme,
  mapLabel,
  resolveMapIdAlias,
} from "@/lib/office/map-registry";

/** Full map geometry document — published layout SSOT at runtime. */
export type MapLayoutDocument = {
  /** Stable map id (see MAP_IDS / map-registry). */
  id: string;
  cols: number;
  rows: number;
  offices: PersonalOfficeSlotLayout[];
  commonZones: CommonZoneLayout[];
  furniture: MapFurniture[];
  spawnPoints: Array<{ gx: number; gy: number }>;
  transitions: MapTransition[];
};

import {
  allDefaultOfficeSlots,
  buildAllDefaultLayouts,
  buildCampusLayout,
} from "@/lib/office/campus-layouts";
import { resolveMapIdAlias } from "@/lib/office/map-registry";

/** All personal office pods across default department buildings (42). */
export const PERSONAL_OFFICE_SLOTS: readonly PersonalOfficeSlotLayout[] = allDefaultOfficeSlots();

export const PERSONAL_OFFICE_SLOT_KEYS = PERSONAL_OFFICE_SLOTS.map((s) => s.slotKey);

export type PersonalOfficeSlotKey = (typeof PERSONAL_OFFICE_SLOT_KEYS)[number];

/** @deprecated Use getDefaultLayout("campus") — kept for older imports. */
export const DEFAULT_MAP_LAYOUT: MapLayoutDocument = cloneMapLayout(
  buildCampusLayout() as unknown as MapLayoutDocument,
);

/** @deprecated Use getDefaultLayout("campus"). */
export const DEFAULT_PLAZA_LAYOUT: MapLayoutDocument = cloneMapLayout(
  buildCampusLayout() as unknown as MapLayoutDocument,
);

/** Campus outdoor join map. */
export const DEFAULT_CAMPUS_LAYOUT: MapLayoutDocument = cloneMapLayout(
  buildCampusLayout() as unknown as MapLayoutDocument,
);

/** Return the code-default layout for a map id (aliases hq→reception, plaza→campus). */
export function getDefaultLayout(mapId: string = "campus"): MapLayoutDocument {
  const id = resolveMapIdAlias(mapId);
  const layouts = buildAllDefaultLayouts();
  const layout = layouts[id] ?? layouts.campus ?? buildCampusLayout();
  // Campus builders use string furniture kinds; clone narrows into MapLayoutDocument.
  return cloneMapLayout(layout as unknown as MapLayoutDocument);
}

export function cloneMapLayout(layout: MapLayoutDocument): MapLayoutDocument {
  return {
    id: layout.id || "campus",
    cols: layout.cols,
    rows: layout.rows,
    offices: layout.offices.map((s) => ({
      ...s,
      door: { ...s.door },
      interiorSeat: { ...s.interiorSeat },
      desk: { ...s.desk },
      decorSlots: s.decorSlots.map((d) => ({ ...d })),
      wallArt: { ...s.wallArt },
      wallArtSlots: (s.wallArtSlots ?? [s.wallArt]).map((w) => ({ ...w })),
    })),
    commonZones: layout.commonZones.map((z) => ({ ...z })),
    furniture: layout.furniture.map((f) => ({
      ...f,
      ...(f.c ? { c: f.c } : {}),
      ...(f.displayName ? { displayName: f.displayName } : {}),
      ...(f.doorWall ? { doorWall: f.doorWall } : {}),
      ...(f.style ? { style: f.style } : {}),
    })),
    spawnPoints: layout.spawnPoints.map((p) => ({ ...p })),
    transitions: (layout.transitions ?? []).map((t) => ({
      ...t,
      toSpawn: { ...t.toSpawn },
    })),
  };
}

export function personalOfficeAt(
  gx: number,
  gy: number,
  offices: readonly PersonalOfficeSlotLayout[] = PERSONAL_OFFICE_SLOTS,
): PersonalOfficeSlotLayout | undefined {
  return offices.find((s) => gx >= s.x1 && gx <= s.x2 && gy >= s.y1 && gy <= s.y2);
}

/** True when (gx,gy) is strictly inside a pod (including walls/door). */
export function isInsidePersonalOffice(
  gx: number,
  gy: number,
  offices: readonly PersonalOfficeSlotLayout[] = PERSONAL_OFFICE_SLOTS,
): boolean {
  return personalOfficeAt(gx, gy, offices) !== undefined;
}

/**
 * Whether a user may enter a pod at (gx,gy) given door + ownership state.
 * Unassigned pods are closed to everyone.
 */
export function canEnterPersonalOffice(opts: {
  gx: number;
  gy: number;
  userId: string;
  doorStatus: "open" | "closed";
  ownerUserId: string | null;
  offices?: readonly PersonalOfficeSlotLayout[];
}): boolean {
  const slot = personalOfficeAt(opts.gx, opts.gy, opts.offices);
  if (!slot) return true;
  if (opts.ownerUserId && opts.ownerUserId === opts.userId) return true;
  if (!opts.ownerUserId) return false;
  return opts.doorStatus === "open";
}
